import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Generic repeater for arrays of objects.
 * Children renderer recibe (item, index, update(field, value)) y devuelve los inputs.
 */
function RepeaterField({ label, items = [], onChange, newItem, max, children, itemLabel }) {
  const addItem = () => {
    if (max && items.length >= max) return
    onChange([...items, newItem()])
  }

  const removeItem = (index) => onChange(items.filter((_, i) => i !== index))

  const moveItem = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= items.length) return
    const next = [...items]
    ;[next[index], next[newIndex]] = [next[newIndex], next[index]]
    onChange(next)
  }

  const updateItem = (index, field, value) => {
    const next = items.map((it, i) => (i === index ? { ...it, [field]: value } : it))
    onChange(next)
  }

  const limitReached = max && items.length >= max

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-body font-semibold text-neutral-900 dark:text-neutral-50">
          {label}{' '}
          {max && (
            <span className="text-caption font-normal text-neutral-500 dark:text-neutral-400">
              ({items.length}/{max})
            </span>
          )}
        </h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          disabled={limitReached}
        >
          <Plus size={14} /> Agregar
        </Button>
      </div>

      {items.length === 0 && (
        <div className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/60 p-6 text-center">
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
            Aún no has agregado nada. Toca &quot;Agregar&quot; para empezar.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 sm:p-4"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-body-sm font-medium text-neutral-600 dark:text-neutral-400">
                {itemLabel ? itemLabel(item, idx) : `${label.replace(/s$/, '')} #${idx + 1}`}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 dark:text-neutral-400 transition-colors',
                    idx === 0
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-50'
                  )}
                  aria-label="Subir"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 dark:text-neutral-400 transition-colors',
                    idx === items.length - 1
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-50'
                  )}
                  aria-label="Bajar"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15 transition-colors"
                  aria-label="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {children(item, idx, (field, value) => updateItem(idx, field, value))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default RepeaterField
